package com.islamicmedia.site.components

import androidx.compose.runtime.*
import com.islamicmedia.shared.domain.model.ReferralInfo
import com.islamicmedia.shared.domain.model.ReferralStats
import com.islamicmedia.shared.presentation.ReferralCopy
import com.islamicmedia.site.theme.*
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLTextAreaElement
import kotlin.js.Promise

/**
 * The Refer a friend screen (Index's ActiveView.REFERRAL), opened from the avatar menu, which only
 * offers it when the entitlement says can_refer. The member's code big, their link under it, Copy
 * and Share, then COUNTS: friends joined, free months earned, the year's cap and when the next month
 * unlocks. Never a friend's address; the server doesn't send one.
 *
 * Same frame as the other menu screens (Help, Account): 32px padding and a centred title level with
 * the card's ← and ✕, which do the navigating. Every word comes from ReferralCopy, shared with
 * composeApp/.../components/ReferralScreen.kt — keep the two in step.
 *
 * [info] is the last GET /api/referral/me answer. The ViewModel keeps it while a reload runs, so a
 * reopen shows the old counts at once; the spinner and the error only show when there's nothing to
 * show instead.
 *
 * [onOpenRedeem] drives "Got a code from a friend?" — shown only while `info?.canRedeem == true` —
 * for a member who subscribed without attaching a code at the paywall (typically a ThriveCart
 * buyer). The dialog it opens (MediaCheckerUiState.showRedeemCodeModal) is rendered by Index.kt at
 * the top level, not here, so the SAME dialog also serves the home screen's proactive "Did a
 * friend send you a code?" popup — see MediaCheckerViewModel's redeem section. Mirrors
 * composeApp/.../components/ReferralScreen.kt — keep the two in step.
 */
@Composable
fun ReferralScreen(
    info: ReferralInfo?,
    loading: Boolean,
    error: String?,
    onRetry: () -> Unit,
    /** "Copy link" put the link on the clipboard. */
    onCopied: () -> Unit,
    /** The link went out: "web_share" (the browser's share sheet) or "copy_fallback" (no sheet, so
     *  Share copied it instead). */
    onShared: (method: String) -> Unit,
    onOpenRedeem: () -> Unit = {},
) {
    Div(attrs = {
        style {
            padding(32.px)
            property("box-sizing", "border-box")
            property("text-align", "center")
        }
    }) {
        // Title — same treatment as the Help screen's and ProfileSubscreenScaffold's.
        Div(attrs = {
            style {
                marginBottom(18.px)
                fontSize(14.px)
                fontWeight(600)
                color(Gray900)
                property("letter-spacing", "0.02em")
            }
        }) { Text(ReferralCopy.TITLE) }

        Div(attrs = { style { property("max-width", "360px"); property("margin", "0 auto") } }) {
            when {
                // Checked first so a reload keeps showing the counts it already has.
                info?.isOk == true -> ReferralDetails(info, onCopied, onShared, onOpenRedeem)
                // A reload also hides a stale "not eligible": someone who met the paywall before
                // subscribing would otherwise be told to subscribe until the fresh answer lands.
                loading || (info == null && error == null) -> ReferralSpinner()
                error != null -> ReferralRetry(onRetry)
                info?.isNotEligible == true -> ReferralMessage(ReferralCopy.NOT_ELIGIBLE)
                info?.isDisabled == true -> ReferralMessage(ReferralCopy.UNAVAILABLE)
                // An answer we don't understand (an "ok" without a code, a new status).
                else -> ReferralRetry(onRetry)
            }
        }
    }
}

/** Confirmation after a successful redeem, same frame as [ReferralCodeDialog] / the voucher success
 *  modal but its own thing: closing it just clears the one-time flag. Rendered by Index.kt at the
 *  top level (alongside [ReferralCodeDialog] itself), not here — see the redeem dialog's own note
 *  above — so it's public rather than private. */
@Composable
fun ReferralRedeemSuccessModal(message: String, onConsumed: () -> Unit) {
    Div(attrs = {
        onClick { onConsumed() }
        style {
            position(Position.Fixed); top(0.px); left(0.px); right(0.px); bottom(0.px)
            backgroundColor(rgba(0, 0, 0, 0.5))
            display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
            property("z-index", "3100"); padding(16.px)
        }
    }) {
        Div(attrs = {
            onClick { it.stopPropagation() }
            style {
                width(100.percent); property("max-width", "340px")
                backgroundColor(Color.white); borderRadius(20.px); padding(24.px)
                property("box-sizing", "border-box"); property("text-align", "center")
                property("box-shadow", "0 20px 60px -20px rgba(0,0,0,0.45)")
            }
        }) {
            Div(attrs = { style { fontSize(32.px) } }) { Text("🎁") }
            P(attrs = {
                style { margin(10.px, 0.px, 0.px); fontSize(14.px); color(Gray600); property("line-height", "21px") }
            }) { Text(message) }
            Button(attrs = {
                onClick { onConsumed() }
                style {
                    property("margin-top", "18px"); width(100.percent); padding(12.px)
                    fontSize(15.px); fontWeight(700); color(Color.white); backgroundColor(Primary)
                    borderRadius(14.px); property("border", "none"); cursor("pointer")
                }
            }) { Text("Great") }
        }
    }
}

@Composable
private fun ReferralDetails(
    info: ReferralInfo,
    onCopied: () -> Unit,
    onShared: (method: String) -> Unit,
    onOpenRedeem: () -> Unit,
) {
    val link = info.link?.takeIf { it.isNotBlank() }
    val stats = info.stats ?: ReferralStats()

    // "Copy link" reads "Link copied" for a moment, so the click visibly did something.
    var copied by remember { mutableStateOf(false) }
    LaunchedEffect(copied) {
        if (copied) {
            delay(2000)
            copied = false
        }
    }

    P(attrs = {
        style { margin(0.px); fontSize(14.px); color(Gray600); property("line-height", "21px") }
    }) { Text(ReferralCopy.BODY) }

    Div(attrs = {
        style { marginTop(20.px); fontSize(12.px); fontWeight(600); color(Gray500) }
    }) { Text(ReferralCopy.YOUR_CODE) }

    // The code, with the link under it, on pale teal.
    Div(attrs = {
        style {
            marginTop(6.px)
            padding(16.px, 14.px)
            borderRadius(12.px)
            backgroundColor(PrimaryPale)
            property("border", "1px solid rgba(14,124,123,0.25)")
        }
    }) {
        Div(attrs = {
            style {
                fontSize(28.px)
                fontWeight(700)
                color(Primary)
                property("letter-spacing", "2px")
                // One click selects the whole code: someone reading it out, or typing it into
                // another device, wants the code itself rather than the link.
                property("user-select", "all")
            }
        }) { Text(info.displayCode.orEmpty()) }
        if (link != null) {
            Div(attrs = {
                style {
                    marginTop(4.px)
                    fontSize(12.px)
                    color(Gray500)
                    property("white-space", "nowrap")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                }
            }) { Text(link) }
        }
    }

    // "Got a code from a friend?" — offered only while the server says this member could still
    // redeem one (a new subscriber, still in their first month, who hasn't already used a code).
    // Subtle on purpose: most people opening this screen are here to SHARE their own code, not enter
    // someone else's. Same treatment as the paywall's "Have a referral code?" link.
    if (info.canRedeem) {
        Div(attrs = {
            onClick { onOpenRedeem() }
            style {
                marginTop(10.px)
                property("text-align", "center")
                fontSize(13.px); fontWeight(600); color(Primary)
                property("text-decoration", "underline")
                cursor("pointer")
            }
        }) { Text(ReferralCopy.REDEEM_LINK) }
    }

    // Copy (outlined) and Share (filled), side by side.
    Div(attrs = { style { display(DisplayStyle.Flex); gap(10.px); marginTop(14.px) } }) {
        Button(attrs = {
            if (link != null) onClick {
                copyToClipboard(link) { ok ->
                    if (ok) {
                        copied = true
                        onCopied()
                    }
                }
            } else attr("disabled", "true")
            style {
                referralButtonStyle()
                color(Primary)
                backgroundColor(Color.white)
                property("border", "1px solid $Primary")
            }
        }) {
            ReferralButtonIcon(COPY_ICON_PATH, "0E7C7B")
            Text(if (copied) ReferralCopy.COPIED else ReferralCopy.COPY_BUTTON)
        }
        Button(attrs = {
            if (link != null) onClick {
                shareLink(info.displayCode.orEmpty(), link) { outcome ->
                    when (outcome) {
                        ShareOutcome.SHARED -> onShared("web_share")
                        // No share sheet: it copied instead, so say so the same way Copy does.
                        ShareOutcome.COPIED -> {
                            copied = true
                            onShared("copy_fallback")
                        }
                        ShareOutcome.NOTHING -> Unit
                    }
                }
            } else attr("disabled", "true")
            style {
                referralButtonStyle()
                color(Color.white)
                backgroundColor(Primary)
                property("border", "1px solid $Primary")
            }
        }) {
            ReferralButtonIcon(SHARE_ICON_PATH, "ffffff")
            Text(ReferralCopy.SHARE_BUTTON)
        }
    }

    // Counts, in the Account screen's card style.
    Div(attrs = {
        style {
            marginTop(24.px)
            borderRadius(12.px)
            backgroundColor(Gray100)
            property("border", "1px solid $Gray200")
            property("overflow", "hidden")
        }
    }) {
        ReferralStatRow(ReferralCopy.STAT_JOINED, stats.joined)
        Div(attrs = { style { height(1.px); backgroundColor(Gray200) } })
        ReferralStatRow(ReferralCopy.STAT_EARNED, stats.rewarded)
    }
    val nextRelease = remember(stats.nextReleaseAt) { stats.nextReleaseDateLabel() }
    if (nextRelease != null) {
        Div(attrs = {
            style { marginTop(10.px); fontSize(12.px); color(Gray500) }
        }) { Text(ReferralCopy.nextRelease(nextRelease)) }
    }
}

private fun StyleScope.referralButtonStyle() {
    property("flex", "1")
    display(DisplayStyle.Flex)
    alignItems(AlignItems.Center)
    justifyContent(JustifyContent.Center)
    gap(6.px)
    padding(10.px, 8.px)
    borderRadius(10.px)
    fontSize(14.px)
    fontWeight(600)
    property("white-space", "nowrap")
    cursor("pointer")
}

@Composable
private fun ReferralStatRow(label: String, value: Int) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.SpaceBetween)
            padding(12.px, 14.px)
            fontSize(13.px)
        }
    }) {
        Span(attrs = { style { color(Gray500) } }) { Text(label) }
        Span(attrs = { style { fontWeight(600); color(Gray900) } }) { Text("$value") }
    }
}

/** A plain centred sentence: not eligible, or the program switched off. */
@Composable
private fun ReferralMessage(text: String) {
    P(attrs = {
        style { margin(24.px, 0.px); fontSize(14.px); color(Gray600); property("line-height", "21px") }
    }) { Text(text) }
}

/** The load failed and there's nothing older to show instead. */
@Composable
private fun ReferralRetry(onRetry: () -> Unit) {
    Div(attrs = { style { padding(24.px, 0.px) } }) {
        P(attrs = {
            style { margin(0.px); fontSize(14.px); color(Gray600); property("line-height", "21px") }
        }) { Text(ReferralCopy.ERR_GENERIC) }
        Button(attrs = {
            onClick { onRetry() }
            style {
                marginTop(14.px)
                padding(9.px, 20.px)
                fontSize(13.px)
                color(Color.white)
                backgroundColor(Primary)
                property("border", "none")
                borderRadius(10.px)
                cursor("pointer")
            }
        }) { Text(ReferralCopy.RETRY) }
    }
}

@Composable
private fun ReferralSpinner() {
    InjectSpinKeyframes()
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            padding(40.px, 0.px)
        }
    }) {
        Div(attrs = {
            style {
                width(22.px)
                height(22.px)
                property("box-sizing", "border-box")
                property("border", "2px solid $Gray200")
                property("border-top-color", Primary.toString())
                borderRadius(50.percent)
                property("animation", "spin 0.8s linear infinite")
            }
        })
    }
}

// Material "content_copy" and "share" (24px grid) — the icons mobile's buttons use.
private const val COPY_ICON_PATH =
    "M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"
private const val SHARE_ICON_PATH =
    "M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 " +
        "1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 " +
        "3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"

private external fun encodeURIComponent(value: String): String

/** A 16px icon drawn as an SVG data-URI (the security hook blocks innerHTML — same approach as
 *  PaywallOverlay's StepIcon). [hex] is the fill, without the #. */
@Composable
private fun ReferralButtonIcon(path: String, hex: String) {
    val svg = "<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24'>" +
        "<path fill='#$hex' d='$path'/></svg>"
    Img(src = "data:image/svg+xml," + encodeURIComponent(svg), attrs = {
        attr("alt", "")
        attr("aria-hidden", "true")
        style { width(16.px); height(16.px); property("flex-shrink", "0") }
    })
}

// ---------------------------------------------------------------------------
// Copy and share (there was no web helper for either before this screen)
// ---------------------------------------------------------------------------

private enum class ShareOutcome { SHARED, COPIED, NOTHING }

/**
 * Put [text] on the clipboard. The async Clipboard API first; when it is missing (an old browser,
 * or a page that isn't a secure context) or refuses, the old hidden-textarea + execCommand('copy')
 * route. [onDone] gets whether either worked.
 */
private fun copyToClipboard(text: String, onDone: (Boolean) -> Unit) {
    val clipboard = try { window.navigator.asDynamic().clipboard } catch (_: Throwable) { null }
    if (clipboard != null && jsTypeOf(clipboard.writeText) == "function") {
        try {
            clipboard.writeText(text).unsafeCast<Promise<Any?>>().then(
                { onDone(true) },
                { onDone(legacyCopy(text)) }
            )
            return
        } catch (_: Throwable) { /* fall through to the textarea route */ }
    }
    onDone(legacyCopy(text))
}

/** The pre-Clipboard-API copy: select the text in an off-screen textarea and execCommand('copy'). */
private fun legacyCopy(text: String): Boolean = try {
    val area = document.createElement("textarea") as HTMLTextAreaElement
    area.value = text
    // readonly keeps iOS from raising the keyboard; fixed + transparent keeps the page from jumping.
    area.setAttribute("readonly", "")
    area.style.position = "fixed"
    area.style.top = "0"
    area.style.left = "0"
    area.style.opacity = "0"
    document.body?.appendChild(area)
    area.select()
    area.setSelectionRange(0, text.length)
    val ok = document.asDynamic().execCommand("copy") == true
    document.body?.removeChild(area)
    ok
} catch (_: Throwable) {
    false
}

/**
 * Share [link] (with [code]) with the browser's own share sheet (Web Share API: phones, and some
 * desktops). Without one — most desktop browsers — copy the link instead. Cancelling the sheet does
 * nothing; any other refusal falls back to copying.
 */
private fun shareLink(code: String, link: String, onOutcome: (ShareOutcome) -> Unit) {
    val nav = try { window.navigator.asDynamic() } catch (_: Throwable) { null }
    if (nav != null && jsTypeOf(nav.share) == "function") {
        try {
            // The message already ends with the link, so it goes as text alone: passing it as
            // `url` too makes some share targets print it twice. Mobile sends the same text.
            val data = js("({})")
            data.text = ReferralCopy.shareText(code, link)
            nav.share(data).unsafeCast<Promise<Any?>>().then(
                { onOutcome(ShareOutcome.SHARED) },
                { err ->
                    if (err.asDynamic().name == "AbortError") onOutcome(ShareOutcome.NOTHING)
                    else copyToClipboard(link) { ok -> onOutcome(if (ok) ShareOutcome.COPIED else ShareOutcome.NOTHING) }
                }
            )
            return
        } catch (_: Throwable) { /* fall through to copying */ }
    }
    copyToClipboard(link) { ok -> onOutcome(if (ok) ShareOutcome.COPIED else ShareOutcome.NOTHING) }
}
